import type { ItemMeta } from "../../../shared/types";
import { downloadUrl, formatBytes, formatTime } from "../api";
import { splitLinks } from "../linkify";
import { Icon, type IconName } from "./icons";

function fileIconName(mime: string | undefined): IconName {
  if (!mime) return "file-earmark";
  if (mime.startsWith("image/")) return "file-earmark-image";
  if (mime.startsWith("video/")) return "file-earmark-play";
  if (mime.startsWith("audio/")) return "file-earmark-music";
  if (mime === "application/pdf") return "file-earmark-pdf";
  if (/zip|compressed|tar|7z/.test(mime)) return "file-earmark-zip";
  if (mime.startsWith("text/")) return "file-earmark-text";
  return "file-earmark";
}

function linkified(text: string) {
  return splitLinks(text).map((part, index) =>
    part.href ? (
      <a key={index} href={part.href} target="_blank" rel="noopener noreferrer">
        {part.text}
      </a>
    ) : (
      part.text
    ),
  );
}

interface Props {
  item: ItemMeta;
  sessionCode: string;
  /** Kurz hervorheben (gerade neu angekommen) */
  fresh?: boolean;
  onCopy: (item: ItemMeta) => void;
  onDelete: (item: ItemMeta) => void;
}

export function ItemRow({
  item,
  sessionCode,
  fresh = false,
  onCopy,
  onDelete,
}: Props) {
  const isFile = item.kind === "file";
  return (
    <article className={`item card${fresh ? " fresh" : ""}`}>
      {isFile && item.mime?.startsWith("image/") ? (
        <img
          className="item-thumb"
          src={downloadUrl(sessionCode, item.id, true)}
          alt={item.name}
          loading="lazy"
        />
      ) : (
        <div className="item-icon">
          <Icon
            name={
              isFile
                ? fileIconName(item.mime)
                : item.kind === "clip"
                  ? "clipboard"
                  : "sticky"
            }
            size={22}
          />
        </div>
      )}
      <div className="item-body">
        {isFile ? (
          <>
            <div className="item-title">{item.name}</div>
            <div className="item-meta">
              {formatBytes(item.size)} · {formatTime(item.createdAt)}
            </div>
          </>
        ) : (
          <>
            <div className="item-meta">
              {item.kind === "clip" ? "Zwischenablage" : "Notiz"} ·{" "}
              {formatTime(item.createdAt)}
            </div>
            <pre className="item-text">{linkified(item.text ?? "")}</pre>
          </>
        )}
      </div>
      <div className="item-actions">
        {isFile ? (
          <a
            className="icon-btn"
            href={downloadUrl(sessionCode, item.id)}
            download={item.name}
            aria-label="Herunterladen"
            title="Herunterladen"
          >
            <Icon name="download" />
          </a>
        ) : (
          <button
            className="icon-btn"
            onClick={() => onCopy(item)}
            aria-label="Kopieren"
            title="In die Zwischenablage kopieren"
          >
            <Icon name="copy" />
          </button>
        )}
        <button
          className="icon-btn danger"
          onClick={() => onDelete(item)}
          aria-label="Löschen"
          title="Löschen"
        >
          <Icon name="trash3" />
        </button>
      </div>
    </article>
  );
}
